<template>
  <div class="vendor-preview-content vendor-preview-content--glass" v-if="vendor">
    <div class="vendor-preview-content-header-bar vendor-preview-content-header-bar--glass">
      <SecondaryButton
        label="Back"
        icon="bi bi-arrow-left"
        title="Back to Vendors List"
        @click="goBack"
      />
      <h3 class="vendor-preview-content-title">Vendor Preview</h3>
    </div>
    <div class="vendor-preview-content-body vendor-preview-content-body--glass">
      <div v-if="loading" class="vendor-preview-content-loading-indicator vendor-preview-loading-indicator">
        <i class="bi bi-arrow-clockwise"></i> Loading related information...
      </div>
      <div v-else class="vendor-preview-content-sections">
      <!-- Card 1: Basic Information (General Tab) -->
      <div class="vendor-preview-content-section vendor-preview-details-card--glass" v-if="hasBasicInfo">
        <div class="vendor-preview-section-header--glass">
          <h4 class="vendor-preview-content-section-title">BASIC INFORMATION</h4>
        </div>
        <div class="vendor-preview-content-details-list">
          <div class="vendor-preview-content-detail-row" v-if="vendor.vendorId || vendor.id">
            <span class="vendor-preview-content-detail-label">Vendor ID:</span>
            <div class="vendor-preview-content-detail-value-container">
              <span class="vendor-preview-content-detail-value">{{ vendor.vendorId || vendor.id }}</span>
              <button 
                class="vendor-preview-content-detail-copy-btn" 
                @click="copyToClipboard(vendor.vendorId || vendor.id, 'Vendor ID')"
                title="Copy to clipboard"
              >
                <i class="bi bi-clipboard"></i>
              </button>
            </div>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.vendorName || vendor.name">
            <span class="vendor-preview-content-detail-label">Vendor Name:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.vendorName || vendor.name }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.vendorSince">
            <span class="vendor-preview-content-detail-label">Vendor Since:</span>
            <span class="vendor-preview-content-detail-value">{{ formatDate(vendor.vendorSince) }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.email">
            <span class="vendor-preview-content-detail-label">Email:</span>
            <div class="vendor-preview-content-detail-value-container">
              <span class="vendor-preview-content-detail-value">{{ vendor.email }}</span>
              <button 
                class="vendor-preview-content-detail-copy-btn" 
                @click="copyToClipboard(vendor.email, 'Email')"
                title="Copy to clipboard"
              >
                <i class="bi bi-clipboard"></i>
              </button>
            </div>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.phone">
            <span class="vendor-preview-content-detail-label">Phone:</span>
            <div class="vendor-preview-content-detail-value-container">
              <span class="vendor-preview-content-detail-value">{{ vendor.phone }}</span>
              <button 
                class="vendor-preview-content-detail-copy-btn" 
                @click="copyToClipboard(vendor.phone, 'Phone')"
                title="Copy to clipboard"
              >
                <i class="bi bi-clipboard"></i>
              </button>
            </div>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.fax">
            <span class="vendor-preview-content-detail-label">Fax:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.fax }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.webpage">
            <span class="vendor-preview-content-detail-label">Website:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.webpage }}</span>
          </div>
          <div class="vendor-preview-content-detail-row">
            <span class="vendor-preview-content-detail-label">Status:</span>
            <span class="vendor-preview-content-detail-value" :class="vendor.active !== false ? 'active' : 'inactive'">
              {{ vendor.active !== false ? 'Active' : 'Inactive' }}
            </span>
          </div>
          <div class="vendor-preview-content-detail-row">
            <span class="vendor-preview-content-detail-label">Consultants:</span>
            <span class="vendor-preview-content-detail-value">{{ vendorConsultantsDisplay }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.taxId">
            <span class="vendor-preview-content-detail-label">Tax ID:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.taxId }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.openingBalance != null">
            <span class="vendor-preview-content-detail-label">Opening Balance:</span>
            <span class="vendor-preview-content-detail-value price">${{ Number(vendor.openingBalance).toFixed(2) }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.balanceAsOf">
            <span class="vendor-preview-content-detail-label">Balance as of:</span>
            <span class="vendor-preview-content-detail-value">{{ formatDate(vendor.balanceAsOf) }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.balance != null">
            <span class="vendor-preview-content-detail-label">Current Balance:</span>
            <span class="vendor-preview-content-detail-value pending">${{ Number(vendor.balance).toFixed(2) }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.notes">
            <span class="vendor-preview-content-detail-label">Notes:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.notes }}</span>
          </div>
        </div>
      </div>

      <!-- Card 2: Payment & Settings (Details Tab) -->
      <div class="vendor-preview-content-section vendor-preview-details-card--glass" v-if="hasPaymentSettings">
        <div class="vendor-preview-section-header--glass">
          <h4 class="vendor-preview-content-section-title">PAYMENT & SETTINGS</h4>
        </div>
        <div class="vendor-preview-content-details-list">
          <div class="vendor-preview-content-detail-row" v-if="vendor.paymentMethod">
            <span class="vendor-preview-content-detail-label">Payment Method:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.paymentMethod }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.paymentTerm || paymentTermDetails">
            <span class="vendor-preview-content-detail-label">Payment Terms:</span>
            <span class="vendor-preview-content-detail-value">{{ paymentTermDetails?.name || paymentTermDetails?.description || vendor.paymentTerm }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.creditLimit != null">
            <span class="vendor-preview-content-detail-label">Credit Limit:</span>
            <span class="vendor-preview-content-detail-value price">${{ Number(vendor.creditLimit).toFixed(2) }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendorGroupDetails">
            <span class="vendor-preview-content-detail-label">Vendor Group:</span>
            <span class="vendor-preview-content-detail-value">{{ vendorGroupDetails.name || vendorGroupDetails.groupName }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="shippingMethodDetails">
            <span class="vendor-preview-content-detail-label">Shipping Method:</span>
            <span class="vendor-preview-content-detail-value">{{ shippingMethodDetails.name || shippingMethodDetails.methodName }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.bankName">
            <span class="vendor-preview-content-detail-label">Bank Name:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.bankName }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.bankAccountType">
            <span class="vendor-preview-content-detail-label">Bank Account Type:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.bankAccountType }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.bankRoutingNumber">
            <span class="vendor-preview-content-detail-label">Bank Routing Number:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.bankRoutingNumber }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.accountNumber">
            <span class="vendor-preview-content-detail-label">Account Number:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.accountNumber }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.bankBranch">
            <span class="vendor-preview-content-detail-label">Bank Branch:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.bankBranch }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.taxRegistrationNumber">
            <span class="vendor-preview-content-detail-label">Tax Registration Number:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.taxRegistrationNumber }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.taxCode">
            <span class="vendor-preview-content-detail-label">Tax Code:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.taxCode }}</span>
          </div>
          <div class="vendor-preview-content-detail-row" v-if="vendor.serviceTaxRegistrationNo">
            <span class="vendor-preview-content-detail-label">Service Tax Registration No:</span>
            <span class="vendor-preview-content-detail-value">{{ vendor.serviceTaxRegistrationNo }}</span>
          </div>
        </div>
      </div>

      <!-- Card 3: Contact Details (Contacts Tab) -->
      <div class="vendor-preview-content-section vendor-preview-details-card--glass" v-if="hasContacts">
        <div class="vendor-preview-section-header--glass">
          <h4 class="vendor-preview-content-section-title">CONTACT DETAILS</h4>
        </div>
        <div class="vendor-preview-content-details-list">
          <div 
            v-for="(contact, index) in vendor.contacts" 
            :key="index" 
            class="vendor-preview-content-contact-item"
          >
            <div class="vendor-preview-content-detail-row" v-if="contact.name">
              <span class="vendor-preview-content-detail-label">Name:</span>
              <span class="vendor-preview-content-detail-value">{{ contact.name }}</span>
            </div>
            <div class="vendor-preview-content-detail-row" v-if="contact.title">
              <span class="vendor-preview-content-detail-label">Title:</span>
              <span class="vendor-preview-content-detail-value">{{ contact.title }}</span>
            </div>
            <div class="vendor-preview-content-detail-row" v-if="contact.email">
              <span class="vendor-preview-content-detail-label">Email:</span>
              <div class="vendor-preview-content-detail-value-container">
                <span class="vendor-preview-content-detail-value">{{ contact.email }}</span>
                <button 
                  class="vendor-preview-content-detail-copy-btn" 
                  @click="copyToClipboard(contact.email, 'Email')"
                  title="Copy to clipboard"
                >
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
            </div>
            <div class="vendor-preview-content-detail-row" v-if="contact.phone">
              <span class="vendor-preview-content-detail-label">Phone:</span>
              <div class="vendor-preview-content-detail-value-container">
                <span class="vendor-preview-content-detail-value">{{ contact.phone }}</span>
                <button 
                  class="vendor-preview-content-detail-copy-btn" 
                  @click="copyToClipboard(contact.phone, 'Phone')"
                  title="Copy to clipboard"
                >
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
            </div>
            <div class="vendor-preview-content-detail-row" v-if="contact.primary">
              <span class="vendor-preview-content-detail-label">Primary:</span>
              <span class="vendor-preview-content-detail-value">Yes</span>
            </div>
            <div v-if="index < vendor.contacts.length - 1" class="vendor-preview-content-contact-divider"></div>
          </div>
        </div>
      </div>

      <!-- Card 4: Address Information (Addresses Tab) -->
      <div class="vendor-preview-content-section vendor-preview-details-card--glass" v-if="hasAddresses">
        <div class="vendor-preview-section-header--glass">
          <h4 class="vendor-preview-content-section-title">ADDRESS INFORMATION</h4>
        </div>
        <div class="vendor-preview-content-details-list">
          <div 
            v-for="(address, index) in vendor.addresses" 
            :key="index" 
            class="vendor-preview-content-address-item"
          >
            <div class="vendor-preview-content-detail-row" v-if="address.type">
              <span class="vendor-preview-content-detail-label">Type:</span>
              <span class="vendor-preview-content-detail-value">{{ address.type }}</span>
            </div>
            <div class="vendor-preview-content-detail-row" v-if="address.address1">
              <span class="vendor-preview-content-detail-label">Address:</span>
              <span class="vendor-preview-content-detail-value">{{ address.address1 }}</span>
            </div>
            <div class="vendor-preview-content-detail-row" v-if="address.address2">
              <span class="vendor-preview-content-detail-label">Address 2:</span>
              <span class="vendor-preview-content-detail-value">{{ address.address2 }}</span>
            </div>
            <div class="vendor-preview-content-detail-row" v-if="address.city || address.state || address.postalCode">
              <span class="vendor-preview-content-detail-label">City/State/Zip:</span>
              <span class="vendor-preview-content-detail-value">
                {{ [address.city, address.state, address.postalCode].filter(Boolean).join(', ') }}
              </span>
            </div>
            <div class="vendor-preview-content-detail-row" v-if="address.country">
              <span class="vendor-preview-content-detail-label">Country:</span>
              <span class="vendor-preview-content-detail-value">{{ address.country }}</span>
            </div>
            <div v-if="index < vendor.addresses.length - 1" class="vendor-preview-content-address-divider"></div>
          </div>
        </div>
      </div>

      <!-- Documents Section -->
      <div class="vendor-preview-content-section vendor-preview-details-card--glass" v-if="hasDocuments">
        <div class="vendor-preview-section-header--glass">
          <h4 class="vendor-preview-content-section-title">DOCUMENTS</h4>
        </div>
        <div class="vendor-preview-content-documents-grid">
          <div 
            v-for="(document, index) in documents" 
            :key="document.id || index" 
            class="vendor-preview-content-document-item"
          >
            <div class="vendor-preview-content-detail-row">
              <span class="vendor-preview-content-detail-label">File Name:</span>
              <span class="vendor-preview-content-detail-value">{{ document.originalFileName || document.fileName }}</span>
            </div>
            <div class="vendor-preview-content-detail-row" v-if="document.description">
              <span class="vendor-preview-content-detail-label">Description:</span>
              <span class="vendor-preview-content-detail-value">{{ document.description }}</span>
            </div>
            <div class="vendor-preview-content-detail-row vendor-preview-content-document-meta" v-if="document.fileSize || document.uploadedAt || document.mimeType">
              <span class="vendor-preview-content-detail-label">Details:</span>
              <div class="vendor-preview-content-document-meta-info">
                <span v-if="document.fileSize" class="vendor-preview-content-document-meta-item">{{ formatFileSize(document.fileSize) }}</span>
                <span v-if="document.uploadedAt" class="vendor-preview-content-document-meta-item">{{ formatDate(document.uploadedAt) }}</span>
                <span v-if="document.mimeType" class="vendor-preview-content-document-meta-item">{{ document.mimeType }}</span>
              </div>
            </div>
            <div class="vendor-preview-content-detail-row vendor-preview-content-document-action">
              <PrimaryButton
                label="View Document"
                icon="bi bi-eye"
                compact
                title="View/Download document"
                @click="viewDocument(document)"
              />
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
    
    <div class="vendor-preview-content-footer vendor-preview-content-footer--glass" v-if="vendor">
      <PrimaryButton
        label="Edit Vendor"
        icon="bi bi-pencil-square"
        @click="editVendor"
      />
    </div>

    <!-- Document Preview Modal -->
    <Transition name="vendor-preview-document-modal-overlay">
      <div
        v-if="showDocumentPreview"
        class="vendor-preview-document-overlay vendor-preview-document-overlay--glass"
        @click.self="closeDocumentPreview"
      >
        <Transition name="vendor-preview-document-modal-panel" appear>
          <div
            v-if="showDocumentPreview"
            class="vendor-preview-document-modal vendor-preview-document-modal--glass"
            :class="{ 'resizing': isResizingDocument, 'has-custom-size': true }"
            @click.stop
            :style="{ width: documentModalWidth + 'px', height: documentModalHeight + 'px' }"
          >
            <div class="vendor-preview-document-header vendor-preview-document-header--glass">
              <h3>{{ selectedDocument?.originalFileName || selectedDocument?.fileName || 'Document Preview' }}</h3>
              <button
                type="button"
                class="vendor-preview-document-close vendor-preview-document-close--glass"
                aria-label="Close"
                @click="closeDocumentPreview"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </div>
            <div class="vendor-preview-document-body vendor-preview-document-body--glass">
              <div v-if="loadingPreview" class="vendor-preview-document-loading">
                <i class="bi bi-arrow-repeat vendor-preview-document-loading-spinner" aria-hidden="true"></i>
                <p>Loading preview...</p>
              </div>
              <div v-else-if="previewError" class="vendor-preview-document-error">
                <div class="vendor-preview-document-icon">
                  <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
                </div>
                <p>Failed to load preview</p>
                <PrimaryButton
                  label="Download Document"
                  icon="bi bi-download"
                  compact
                  @click="downloadDocument(selectedDocument)"
                />
              </div>
              <div v-else-if="previewUrl && isImageFile(selectedDocument)" class="vendor-preview-document-image">
                <img :src="previewUrl" :alt="selectedDocument?.originalFileName || selectedDocument?.fileName" @error="handlePreviewError" />
              </div>
              <div v-else-if="previewUrl && isPdfFile(selectedDocument)" class="vendor-preview-document-pdf">
                <iframe :src="previewUrl" frameborder="0" @error="handlePreviewError"></iframe>
              </div>
              <div v-else-if="previewUrl" class="vendor-preview-document-unsupported">
                <div class="vendor-preview-document-icon">
                  <i class="bi bi-file-earmark" aria-hidden="true"></i>
                </div>
                <p>Preview not available for this file type</p>
                <PrimaryButton
                  label="Download Document"
                  icon="bi bi-download"
                  compact
                  @click="downloadDocument(selectedDocument)"
                />
              </div>
            </div>
            <div class="vendor-preview-document-footer vendor-preview-document-footer--glass">
              <PrimaryButton
                label="Download"
                icon="bi bi-download"
                @click="downloadDocument(selectedDocument)"
              />
            </div>
            <div class="vendor-preview-content-document-resize-handle" @mousedown.stop="startDocumentResize"></div>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script>
import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';
import CompanyService from '@/services/company.service.js';
import VendorService from '@/services/vendor.service.js';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import './VendorPreviewContent.css';

export default {
  name: 'VendorPreviewContent',
  components: {
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    vendor: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      paymentTermDetails: null,
      vendorGroupDetails: null,
      shippingMethodDetails: null,
      documents: [],
      loading: false,
      showDocumentPreview: false,
      selectedDocument: null,
      previewUrl: '',
      loadingPreview: false,
      previewError: false,
      documentPreviewUrls: {},
      documentModalWidth: 1200,
      documentModalHeight: 800,
      isResizingDocument: false,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 0,
      resizeStartHeight: 0,
      vendorConsultantsCount: null
    };
  },
  computed: {
    vendorConsultantsDisplay() {
      if (this.vendorConsultantsCount === null) return '—';
      return String(this.vendorConsultantsCount);
    },
    hasBasicInfo() {
      return !!(this.vendor?.vendorId || this.vendor?.id || this.vendor?.vendorName
        || this.vendor?.name || this.vendor?.vendorSince || this.vendor?.email
        || this.vendor?.phone || this.vendor?.fax || this.vendor?.webpage
        || this.vendor?.active !== undefined || this.vendor?.openingBalance != null
        || this.vendor?.balance != null || this.vendor?.balanceAsOf || this.vendor?.taxId
        || this.vendor?.notes);
    },
    hasPaymentSettings() {
      return !!(this.vendor?.paymentMethod || this.vendor?.creditLimit != null
        || this.vendor?.paymentTerm || this.vendor?.paymentTermId || this.vendorGroupDetails
        || this.shippingMethodDetails || this.vendor?.bankName || this.vendor?.accountNumber
        || this.vendor?.bankBranch || this.vendor?.bankAccountType || this.vendor?.bankRoutingNumber
        || this.vendor?.taxRegistrationNumber || this.vendor?.taxCode
        || this.vendor?.serviceTaxRegistrationNo);
    },
    hasContacts() {
      return !!(this.vendor?.contacts && Array.isArray(this.vendor.contacts) && this.vendor.contacts.length > 0);
    },
    hasAddresses() {
      return !!(this.vendor?.addresses && Array.isArray(this.vendor.addresses) && this.vendor.addresses.length > 0);
    },
    hasDocuments() {
      return !!(this.documents && Array.isArray(this.documents) && this.documents.length > 0);
    },
  },
  watch: {
    vendor: {
      handler(newVendor) {
        if (newVendor) {
          this.fetchRelatedData();
        } else {
          this.documents = [];
          this.vendorConsultantsCount = null;
        }
      },
      immediate: true
    }
  },
  methods: {
    editVendor() {
      this.$emit('edit');
    },
    goBack() {
      this.$emit('close');
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    
    async copyToClipboard(text, label) {
      if (!text) return;
      
      try {
        await navigator.clipboard.writeText(text);
        this.$toast && this.$toast.success(`${label} copied to clipboard`);
      } catch (error) {
        logger.error('Failed to copy to clipboard:', error);
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.$toast && this.$toast.success(`${label} copied to clipboard`);
        } catch (fallbackError) {
          logger.error('Fallback copy failed:', fallbackError);
          this.$toast && this.$toast.error('Failed to copy to clipboard');
        }
        document.body.removeChild(textArea);
      }
    },
    
    async fetchRelatedData() {
      if (!this.vendor) {
        this.documents = [];
        this.vendorConsultantsCount = null;
        return;
      }
      
      this.loading = true;
      this.vendorConsultantsCount = null;
      
      try {
        const companyId = this.$route?.params?.id || this.$route?.params?.companyId;
        const vendorId = this.vendor.id || this.vendor.vendorId;
        
        if (!companyId || !vendorId) {
          logger.error('Company ID or Vendor ID not found', { companyId, vendorId });
          this.loading = false;
          return;
        }
        
        // Use same approach as EditVendor: fetch full vendor data with documents included
        if (this.vendor.documents && Array.isArray(this.vendor.documents) && this.vendor.documents.length > 0) {
          this.documents = (this.vendor.documents || []).map(doc => ({
            id: doc.id,
            fileName: doc.fileName || '',
            originalFileName: doc.originalFileName || doc.fileName || '',
            filePath: doc.filePath || '',
            fileSize: doc.fileSize || 0,
            mimeType: doc.mimeType || '',
            uploadedAt: doc.uploadedAt || doc.createdAt || '',
            description: doc.description || '',
            vendorId: doc.vendorId,
            companyId: doc.companyId
          }));
        } else {
          const response = await VendorService.getVendorById(vendorId, companyId);
          const vendorData = response.data || response;
          
          if (vendorData && vendorData.documents && Array.isArray(vendorData.documents)) {
            this.documents = vendorData.documents.map(doc => ({
              id: doc.id,
              fileName: doc.fileName || '',
              originalFileName: doc.originalFileName || doc.fileName || '',
              filePath: doc.filePath || '',
              fileSize: doc.fileSize || 0,
              mimeType: doc.mimeType || '',
              uploadedAt: doc.uploadedAt || doc.createdAt || '',
              description: doc.description || '',
              vendorId: doc.vendorId,
              companyId: doc.companyId
            }));
          } else {
            this.documents = [];
          }
        }
        
        if (this.vendor.paymentTermId) {
          await this.fetchPaymentTermDetails(companyId);
        }
        if (this.vendor.vendorGroupId) {
          await this.fetchVendorGroupDetails(companyId);
        }
        if (this.vendor.shippingMethodId) {
          await this.fetchShippingMethodDetails(companyId);
        }
        
        await this.fetchVendorConsultantsCount(vendorId);
      } catch (error) {
        logger.error('Error fetching related data:', error);
        this.documents = [];
        this.vendorConsultantsCount = null;
      } finally {
        this.loading = false;
      }
    },
    
    async fetchVendorConsultantsCount(vendorId) {
      try {
        const res = await ApiService.get(`/consultant/by-vendor/${vendorId}`);
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        this.vendorConsultantsCount = list.length;
      } catch (error) {
        logger.error('Error fetching consultants for vendor:', error);
        this.vendorConsultantsCount = null;
      }
    },
    
    async fetchPaymentTermDetails(companyId) {
      try {
        const response = await ApiService.get(`/company/payment-terms?companyId=${companyId}`);
        const paymentTerms = Array.isArray(response.data.paymentTerms) ? response.data.paymentTerms : [];
        const paymentTerm = paymentTerms.find(pt => pt.id === this.vendor.paymentTermId);
        if (paymentTerm) {
          this.paymentTermDetails = paymentTerm;
        }
      } catch (error) {
        logger.error('Error fetching payment term details:', error);
      }
    },
    
    async fetchVendorGroupDetails(companyId) {
      try {
        const vendorGroups = await CompanyService.getAllVendorGroups(companyId);
        const vendorGroup = vendorGroups.find(vg => vg.id === this.vendor.vendorGroupId);
        if (vendorGroup) {
          this.vendorGroupDetails = vendorGroup;
        }
      } catch (error) {
        logger.error('Error fetching vendor group details:', error);
      }
    },
    
    async fetchShippingMethodDetails(companyId) {
      try {
        const response = await ApiService.get(`/vendor/preferred-shipping-methods?companyId=${companyId}`);
        const shippingMethods = Array.isArray(response.data) ? response.data : [];
        const shippingMethod = shippingMethods.find(sm => sm.id === this.vendor.shippingMethodId);
        if (shippingMethod) {
          this.shippingMethodDetails = shippingMethod;
        }
      } catch (error) {
        logger.error('Error fetching shipping method details:', error);
      }
    },
    
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    async viewDocument(doc) {
      this.selectedDocument = doc;
      this.showDocumentPreview = true;
      this.previewError = false;
      this.previewUrl = '';
      this.loadingPreview = true;
      
      try {
        const url = await this.getDocumentPreviewUrl(doc);
        if (url) {
          this.previewUrl = url;
          this.previewError = false;
        } else {
          this.previewError = true;
        }
      } catch (error) {
        logger.error('Error loading document preview:', error);
        this.previewError = true;
      } finally {
        this.loadingPreview = false;
      }
    },
    
    async getDocumentPreviewUrl(document) {
      if (!document || !document.filePath) return '';
      
      if (this.documentPreviewUrls[document.filePath]) {
        return this.documentPreviewUrls[document.filePath];
      }
      
      if (document.filePath.startsWith('data:')) {
        this.documentPreviewUrls[document.filePath] = document.filePath;
        return document.filePath;
      }
      
      if (document.id) {
        try {
          const companyId = this.$route?.params?.id || this.$route?.params?.companyId;
          if (!companyId) return '';
          
          const response = await VendorService.viewVendorDocument(document.id, companyId);
          if (!response || !response.data) return '';
          
          const contentType = response.headers && response.headers['content-type'];
          if (contentType && contentType.includes('application/json')) {
            try {
              const text = await response.data.text();
              const errorData = JSON.parse(text);
              throw new Error(errorData.error || errorData.message || 'Failed to load document');
            } catch (parseError) {
              throw new Error('Server returned an error response');
            }
          }
          
          if (response.status && response.status >= 400) {
            throw new Error(`Server returned status ${response.status}`);
          }
          
          const mimeType = contentType || document.mimeType || 'application/pdf';
          const blob = response.data instanceof Blob 
            ? response.data 
            : new Blob([response.data], { type: mimeType });
          
          const url = window.URL.createObjectURL(blob);
          this.documentPreviewUrls[document.filePath] = url;
          return url;
        } catch (error) {
          logger.error('Failed to load document preview:', error);
          return '';
        }
      }
      
      return '';
    },
    
    isImageFile(document) {
      if (!document || !document.mimeType) return false;
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      return imageTypes.includes(document.mimeType.toLowerCase());
    },
    
    isPdfFile(document) {
      if (!document || !document.mimeType) return false;
      return document.mimeType.toLowerCase() === 'application/pdf';
    },
    
    handlePreviewError() {
      this.previewError = true;
      this.previewUrl = '';
    },
    
    closeDocumentPreview() {
      this.showDocumentPreview = false;
      this.selectedDocument = null;
      this.previewUrl = '';
      this.previewError = false;
      this.loadingPreview = false;
    },
    startDocumentResize(e) {
      // Only start resize if clicking on the resize handle
      if (!e.target.classList.contains('vendor-preview-content-document-resize-handle')) {
        return;
      }
      e.preventDefault();
      this.isResizingDocument = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.documentModalWidth;
      this.resizeStartHeight = this.documentModalHeight;
      
      document.addEventListener('mousemove', this.handleDocumentResize);
      document.addEventListener('mouseup', this.stopDocumentResize);
    },
    handleDocumentResize(e) {
      if (!this.isResizingDocument) return;
      
      const deltaX = e.clientX - this.resizeStartX;
      const deltaY = e.clientY - this.resizeStartY;
      
      const newWidth = Math.max(600, Math.min(window.innerWidth - 100, this.resizeStartWidth + deltaX));
      const newHeight = Math.max(400, Math.min(window.innerHeight - 100, this.resizeStartHeight + deltaY));
      
      this.documentModalWidth = newWidth;
      this.documentModalHeight = newHeight;
    },
    stopDocumentResize() {
      this.isResizingDocument = false;
      document.removeEventListener('mousemove', this.handleDocumentResize);
      document.removeEventListener('mouseup', this.stopDocumentResize);
    },
    
    async downloadDocument(doc) {
      try {
        const companyId = this.$route?.params?.id || this.$route?.params?.companyId;
        if (!companyId || !doc.id) return;
        
        const response = await VendorService.viewVendorDocument(doc.id, companyId);
        const blob = new Blob([response.data], { type: doc.mimeType || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.originalFileName || doc.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        logger.error('Error downloading document:', error);
        this.$toast && this.$toast.error('Failed to download document');
      }
    }
  },
  beforeUnmount() {
    // Clean up resize event listeners
    document.removeEventListener('mousemove', this.handleDocumentResize);
    document.removeEventListener('mouseup', this.stopDocumentResize);
    
    // Cleanup blob URLs to prevent memory leaks
    Object.values(this.documentPreviewUrls).forEach(url => {
      if (url && url.startsWith('blob:')) {
        window.URL.revokeObjectURL(url);
      }
    });
  }
}
</script>

<style scoped>
.vendor-preview-content {
  display: flex;
  flex-direction: column;
}

.vendor-preview-content-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e0e0e0;
  background: #f8fafd;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  margin-top: auto;
}
</style>

